/**
 * Screen 1a — sign in with a work email and a six-digit code
 * (`POST /partner/auth/code` → `POST /partner/auth/verify`), or with
 * "Continue with Google" (`POST /partner/auth/google`) when the build has a
 * Google client id. There are no passwords. New and returning partners take
 * the same path either way; what happens next depends on how many
 * organisations the email belongs to (none → the create-org step, with the
 * Partner Terms).
 */
import { useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { ApiError } from '../api/errors';
import { AgentPicker } from '../agent/AgentPicker';
import { NewcomerChooser } from '../agent/NewcomerAgent';
import { DOCS_PATH } from '../agent/handoff';
import { useSession } from '../auth/session';
import { GoogleSignInButton, useGoogleSignInConfig } from '../auth/GoogleSignIn';
import { useRouter } from '../router';
import { rememberStartPath } from '../setup/startPath';
import { Disclosure, ErrorNotice, Field } from '../ui/kit';

function Trace() {
  return (
    <svg className="trace" viewBox="0 0 360 260" aria-hidden="true">
      <defs>
        <linearGradient id="tr" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="var(--brand-a)" />
          <stop offset="1" stopColor="var(--brand-b)" />
        </linearGradient>
      </defs>
      <path d="M10 30 H120 V110 H240 V190 H350" fill="none" stroke="var(--rail-line)" strokeWidth="2" />
      <path d="M10 30 H120 V110 H240" fill="none" stroke="url(#tr)" strokeWidth="2.5" />
      {[
        [10, 30],
        [120, 30],
        [120, 110],
        [240, 110],
      ].map(([x, y], i) => (
        <circle key={i} cx={x} cy={y} r="6" fill="url(#tr)" />
      ))}
      <circle cx="240" cy="190" r="6" fill="var(--rail)" stroke="var(--rail-line)" strokeWidth="2" />
      <circle cx="350" cy="190" r="6" fill="var(--rail)" stroke="var(--rail-line)" strokeWidth="2" />
      <text x="10" y="58" fill="var(--rail-text)" fontSize="11" fontFamily="inherit">photo</text>
      <text x="126" y="98" fill="var(--rail-text)" fontSize="11" fontFamily="inherit">diagnosis</text>
      <text x="246" y="98" fill="var(--rail-text)" fontSize="11" fontFamily="inherit">lead</text>
      <text x="246" y="218" fill="var(--rail-text)" fontSize="11" fontFamily="inherit">your team</text>
    </svg>
  );
}

/**
 * `aside`, when given, takes the place of the decorative trace on the rail:
 * the sign-in screen puts the newcomer's three-path chooser there, beside the
 * email form (above it on a phone).
 */
export const SITE_PITCH =
  'Put the FinderlyFix diagnosis widget on your site. Homeowners snap the problem; your team gets a lead with the photo and the diagnosis already done.';
/** The rail for someone who came for a page of their own (`?start=page`, or the chooser's page tab). */
export const PAGE_PITCH =
  'A free page for your business on FinderlyFix. Homeowners find you, send a photo of the problem, and you get the lead.';
export const PAGE_TITLE = 'Your page. Your leads.';

export function AuthFrame({
  children,
  title,
  aside,
  pitch = SITE_PITCH,
}: {
  children: ReactNode;
  title: string;
  aside?: ReactNode;
  pitch?: string;
}) {
  return (
    <div className="auth">
      <aside className="auth-side">
        <div className="brand" style={{ color: 'var(--rail-strong)' }}>
          <span>
            FinderlyFix <small>Partners</small>
          </span>
        </div>
        <div className="stack">
          <h1>{title}</h1>
          <p className="pitch" style={{ maxWidth: '38ch', lineHeight: '22px' }}>
            {pitch}
          </p>
        </div>
        {aside ?? <Trace />}
        <p className="small pitch">© {new Date().getFullYear()} AI Vision Technologies Inc</p>
      </aside>
      <main className="auth-main" id="main">
        <div className="auth-card">{children}</div>
      </main>
    </div>
  );
}

/**
 * Who arrives here, and the one thing each does (owner feedback 2026-09-28):
 *
 *   - a newcomer (`NewcomerChooser`, on the rail) picks one of three paths:
 *     "Just try it" (the live demo, no account), "No website" (a page we host:
 *     the email form here, then "A page from us" in org setup; agent sign-up
 *     needs a website to prove, so it cannot serve this path), or "My
 *     website" (ONE prompt for a coding agent, which signs up, installs and
 *     sends a test lead by itself);
 *   - someone without an agent, or returning: the email form;
 *   - an existing partner connecting an agent: "Already a partner? Connect
 *     your agent", closed by default, with the per-client picker (browser
 *     sign-in, so nothing secret is shown).
 *
 * The agent parts are hidden while this sign-in is an agent's own round trip
 * (`next=/oauth/consent…`) and while the named person is claiming an account
 * an agent set up (`next=/claim…`): both already know what they are here for.
 */
export const NO_ACCOUNT_DOCS_PATH = `${DOCS_PATH}#no-account`;

function ConnectExistingAgent() {
  return (
    <Disclosure label="Already a partner? Connect your agent" className="auth-agent">
      <div className="stack-sm">
        <p className="small muted">Most agents open your browser so you can sign in and approve them; nothing secret is shown here.</p>
        <AgentPicker />
        <a className="small" href={DOCS_PATH}>
          How it works
        </a>
      </div>
    </Disclosure>
  );
}

/** The email a claim link (`/claim?email=…`) or `?email=` carries, for the form. */
export function prefillEmail(search: URLSearchParams): string {
  const direct = search.get('email');
  if (direct) return direct.trim();
  const next = search.get('next') ?? '';
  const q = next.indexOf('?');
  if (q < 0) return '';
  return (new URLSearchParams(next.slice(q + 1)).get('email') ?? '').trim();
}

export function SignIn() {
  const { requestCode, verifyCode, signInWithGoogle, endedReason } = useSession();
  const google = Boolean(useGoogleSignInConfig().clientId);
  const router = useRouter();
  const [email, setEmail] = useState(() => prefillEmail(router.search));
  const claiming = (router.search.get('next') ?? '').startsWith('/claim');
  const [challenge, setChallenge] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [error, setError] = useState<ApiError | null>(null);
  const [pending, setPending] = useState(false);
  const [googlePending, setGooglePending] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const connectingAgent = (router.search.get('next') ?? '').startsWith('/oauth/');
  // `?start=page`: the homepage's "No website" path links here with it.
  const startsWithPage = router.search.get('start') === 'page';
  useEffect(() => {
    if (startsWithPage) rememberStartPath('page');
  }, [startsWithPage]);
  const emailRef = useRef<HTMLInputElement>(null);
  // The rail reads as the path the person is on: the contractor page once
  // they came for one or picked its tab, the widget otherwise.
  const [pagePath, setPagePath] = useState(startsWithPage);
  // "Get a free page" → "Start with my email": org setup will offer the hosted page first.
  const startPage = () => {
    rememberStartPath('page');
    setPagePath(true);
    emailRef.current?.focus();
    emailRef.current?.scrollIntoView?.({ block: 'center', behavior: 'smooth' });
  };

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = window.setTimeout(() => setCooldown(cooldown - 1), 1000);
    return () => window.clearTimeout(t);
  }, [cooldown]);

  const send = async (e?: FormEvent) => {
    e?.preventDefault();
    setPending(true);
    setError(null);
    try {
      const res = await requestCode(email.trim());
      setChallenge(res.challengeId);
      setCooldown(60);
    } catch (err) {
      setError(err as ApiError);
      const retry = (err as ApiError).body?.retryAfterSec;
      if (typeof retry === 'number') setCooldown(retry);
    } finally {
      setPending(false);
    }
  };

  const withGoogle = async (credential: string) => {
    setGooglePending(true);
    setError(null);
    try {
      await signInWithGoogle(credential);
    } catch (err) {
      setError(err as ApiError);
    } finally {
      setGooglePending(false);
    }
  };

  const verify = async (e: FormEvent) => {
    e.preventDefault();
    if (!challenge) return;
    setPending(true);
    setError(null);
    try {
      await verifyCode(challenge, code.trim());
    } catch (err) {
      setError(err as ApiError);
    } finally {
      setPending(false);
    }
  };

  const showAgent = !challenge && !connectingAgent && !claiming;
  return (
    <AuthFrame
      title={pagePath ? PAGE_TITLE : 'Diagnoses in. Leads out.'}
      pitch={pagePath ? PAGE_PITCH : SITE_PITCH}
      aside={
        showAgent ? (
          <NewcomerChooser google={google} onStartPage={startPage} onPath={(p) => setPagePath(p === 'page')} initial={startsWithPage ? 'page' : 'site'} />
        ) : undefined
      }
    >
      {!challenge ? (
        <>
          <form className="stack" onSubmit={send} noValidate>
            <div className="stack-sm">
              <h2 id="page-title" tabIndex={-1}>
                Sign in or create an account
              </h2>
              <p className="muted">
                {google ? 'Continue with Google, or we’ll email you a six-digit code. No password.' : 'We’ll email you a six-digit code. No password.'}
              </p>
            </div>
            {endedReason ? <div className="notice warn" role="status">{endedReason}</div> : null}
            {claiming ? (
              <div className="notice info" role="status">
                Sign in with the email your AI agent named to claim the account it set up.
              </div>
            ) : null}
            {google ? (
              <>
                <GoogleSignInButton onCredential={(credential) => void withGoogle(credential)} />
                {googlePending ? (
                  <p className="muted small" role="status">
                    Signing you in with Google…
                  </p>
                ) : null}
                <div className="auth-or" role="separator" aria-label="or">
                  <span>or</span>
                </div>
              </>
            ) : null}
            <Field label="Email">
              {(p) => (
                <input
                  {...p}
                  ref={emailRef}
                  className="input input-lg"
                  type="email"
                  autoComplete="email"
                  inputMode="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                />
              )}
            </Field>
            <ErrorNotice error={error} />
            <button className="btn btn-primary btn-lg" type="submit" disabled={pending || googlePending || !/.+@.+\..+/.test(email)}>
              {pending ? 'Sending…' : 'Email me a code'}
            </button>
          </form>
          {showAgent ? <ConnectExistingAgent /> : null}
        </>
      ) : (
        <form className="stack" onSubmit={verify} noValidate>
          <div className="stack-sm">
            <h2 id="page-title" tabIndex={-1}>
              Check your email
            </h2>
            <p className="muted">
              We sent a code to <strong style={{ color: 'var(--ink)' }}>{email}</strong>. It works for 10 minutes.
            </p>
          </div>
          <Field label="Six-digit code">
            {(p) => (
              <input
                {...p}
                className="input input-lg code-input"
                inputMode="numeric"
                autoComplete="one-time-code"
                pattern="[0-9]{6}"
                maxLength={6}
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                autoFocus
              />
            )}
          </Field>
          <ErrorNotice error={error} />
          <button className="btn btn-primary btn-lg" type="submit" disabled={pending || code.length !== 6}>
            {pending ? 'Checking…' : 'Continue'}
          </button>
          <div className="row-between small">
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => { setChallenge(null); setCode(''); setError(null); }}>
              Use a different email
            </button>
            <button type="button" className="btn btn-ghost btn-sm" disabled={cooldown > 0 || pending} onClick={() => void send()}>
              {cooldown > 0 ? `Resend in ${cooldown}s` : 'Resend code'}
            </button>
          </div>
        </form>
      )}
    </AuthFrame>
  );
}
